import React, { useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Shared WYSIWYG editor for Blog, Guide, and Documentation bodies — plain
// controlled-component wrapper around react-quill, producing rich HTML
// stored directly on the model's `body` field.
//
// Requires `react-quill` to be installed in this app (not auto-installed —
// run `npm install react-quill` here before using any page that imports this).

// Custom "callout" block format — renders as <div class="ql-callout"> so the
// public site's .rich-content CSS can style it as a "Key takeaway" box,
// matching the blog redesign. Registered once at module load.
const Block = Quill.import('blots/block');
class CalloutBlot extends Block {}
CalloutBlot.blotName = 'callout';
CalloutBlot.tagName = 'div';
CalloutBlot.className = 'ql-callout';
Quill.register(CalloutBlot);

const modules = {
  toolbar: {
    container: [
      ['addSection'],
      [{ header: [2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['blockquote', 'code-block', 'callout'],
      ['clean'],
    ],
    handlers: {
      callout() {
        const range = this.quill.getSelection(true);
        if (!range) return;
        const format = this.quill.getFormat(range);
        this.quill.format('callout', !format.callout);
      },
      // Inserts a new Heading 2 line pre-filled with placeholder text that's
      // selected so typing replaces it immediately — this is what shows up
      // as a jump-link in the public article's "In This Article" sidebar,
      // so admins get that without needing to know about the Heading 2
      // dropdown themselves.
      addSection() {
        const range = this.quill.getSelection(true);
        const insertAt = range ? range.index : this.quill.getLength();
        const placeholder = 'New section';
        this.quill.insertText(insertAt, '\n' + placeholder + '\n', 'user');
        this.quill.formatLine(insertAt + 1, placeholder.length, 'header', 2, 'user');
        this.quill.setSelection(insertAt + 1, placeholder.length, 'user');
      },
    },
  },
};

// Hover hints for the toolbar buttons (Quill renders them without labels)
const TOOLTIPS = {
  '.ql-addSection': 'Add a new section — its heading becomes a jump-link in "In This Article"',
  '.ql-header': 'Text style',
  '.ql-bold': 'Bold',
  '.ql-italic': 'Italic',
  '.ql-underline': 'Underline',
  '.ql-strike': 'Strikethrough',
  '.ql-list[value="ordered"]': 'Numbered list',
  '.ql-list[value="bullet"]': 'Bullet list',
  '.ql-link': 'Insert link',
  '.ql-image': 'Insert image',
  '.ql-blockquote': 'Quote',
  '.ql-code-block': 'Code block',
  '.ql-callout': 'Highlight as a "Key takeaway" box',
  '.ql-clean': 'Remove formatting',
};

const stripTags = (html) =>
  String(html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();

const STYLES = `
  .rte { border: 1px solid #d1d5db; border-radius: 12px; background: #fff; }
  .rte:focus-within { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.15); }
  .rte .ql-toolbar.ql-snow, .rte .ql-container.ql-snow { border: 0; }

  /* Toolbar — stays visible while scrolling a long article */
  .rte .ql-toolbar.ql-snow {
    position: sticky; top: -20px; z-index: 5;
    display: flex; flex-wrap: wrap; align-items: center; gap: 4px 2px;
    padding: 8px 10px; background: #f9fafb; border-bottom: 1px solid #e5e7eb;
    border-radius: 12px 12px 0 0; font-family: inherit;
  }
  .rte .ql-toolbar.ql-snow .ql-formats { display: inline-flex; align-items: center; margin: 0; padding: 0 6px; border-right: 1px solid #e5e7eb; }
  .rte .ql-toolbar.ql-snow .ql-formats:last-child { border-right: 0; }
  .rte .ql-toolbar.ql-snow button { width: 30px; height: 30px; padding: 6px; border-radius: 6px; }
  .rte .ql-toolbar.ql-snow button:hover { background: #eef2ff; }
  .rte .ql-toolbar.ql-snow button.ql-active { background: #e0e7ff; }

  /* "+ Add section" and "Key takeaway" as labelled pill buttons */
  .rte .ql-toolbar.ql-snow button.ql-addSection,
  .rte .ql-toolbar.ql-snow button.ql-callout {
    width: auto; height: 30px; padding: 0 12px; border-radius: 8px;
    font-size: 12.5px; font-weight: 600; white-space: nowrap; line-height: 30px;
  }
  .rte .ql-toolbar.ql-snow button.ql-addSection { background: #4f46e5; color: #fff; }
  .rte .ql-toolbar.ql-snow button.ql-addSection:hover { background: #4338ca; }
  .rte .ql-toolbar.ql-snow button.ql-addSection::before { content: '+ Add section'; }
  .rte .ql-toolbar.ql-snow button.ql-callout { border: 1px solid #c7d2fe; color: #4338ca; background: #fff; }
  .rte .ql-toolbar.ql-snow button.ql-callout.ql-active { background: #e0e7ff; }
  .rte .ql-toolbar.ql-snow button.ql-callout::before { content: '💡 Key takeaway'; }

  /* Text style picker with plain names */
  .rte .ql-snow .ql-picker.ql-header { width: 150px; height: 30px; }
  .rte .ql-snow .ql-picker.ql-header .ql-picker-label { border-radius: 6px; padding-left: 8px; }
  .rte .ql-snow .ql-picker.ql-header .ql-picker-label::before,
  .rte .ql-snow .ql-picker.ql-header .ql-picker-item::before { content: 'Paragraph'; }
  .rte .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
  .rte .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before { content: 'Section heading'; }
  .rte .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
  .rte .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before { content: 'Sub-heading'; }
  .rte .ql-snow .ql-picker-options { border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.12); }

  /* Writing area */
  .rte .ql-container.ql-snow { font-family: inherit; font-size: 15px; }
  .rte .ql-editor { min-height: 320px; padding: 18px 20px; line-height: 1.7; color: #1f2937; }
  .rte .ql-editor.ql-blank::before { left: 20px; right: 20px; font-style: normal; color: #9ca3af; }
  .rte .ql-editor h2 { font-size: 1.35em; font-weight: 700; margin: 1.2em 0 .4em; padding-left: 10px; border-left: 3px solid #6366f1; }
  .rte .ql-editor h3 { font-size: 1.12em; font-weight: 600; margin: 1em 0 .3em; }
  .rte .ql-editor p { margin-bottom: .6em; }
  .rte .ql-editor a { color: #4f46e5; }
  .rte .ql-editor img { max-width: 100%; border-radius: 8px; }
  .rte .ql-editor blockquote { border-left: 3px solid #d1d5db; color: #4b5563; padding-left: 12px; }
  .rte .ql-editor .ql-callout { background: #eef2ff; border-left: 3px solid #4f46e5; border-radius: 8px; padding: 10px 14px; margin: 8px 0; }

  /* Footer: outline + counts */
  .rte-footer { border-top: 1px solid #e5e7eb; background: #f9fafb; border-radius: 0 0 12px 12px; padding: 10px 14px; }

  html.dark .rte { background: #111827; border-color: #374151; }
  html.dark .rte .ql-toolbar.ql-snow, html.dark .rte-footer { background: #1f2937; border-color: #374151; }
  html.dark .rte .ql-toolbar.ql-snow .ql-formats { border-color: #374151; }
  html.dark .rte .ql-toolbar.ql-snow .ql-stroke { stroke: #d1d5db; }
  html.dark .rte .ql-toolbar.ql-snow .ql-fill { fill: #d1d5db; }
  html.dark .rte .ql-toolbar.ql-snow .ql-picker { color: #d1d5db; }
  html.dark .rte .ql-snow .ql-picker-options { background: #1f2937; border-color: #374151; }
  html.dark .rte .ql-toolbar.ql-snow button:hover, html.dark .rte .ql-toolbar.ql-snow button.ql-active { background: rgba(99,102,241,.2); }
  html.dark .rte .ql-toolbar.ql-snow button.ql-callout { background: transparent; border-color: #4338ca; color: #a5b4fc; }
  /* The panel's global dark-mode text colours would otherwise win here */
  html.dark .rte .ql-editor, html.dark .rte .ql-editor p, html.dark .rte .ql-editor li { color: #e5e7eb; }
  html.dark .rte .ql-editor h2, html.dark .rte .ql-editor h3 { color: #f9fafb; }
  html.dark .rte .ql-editor blockquote { color: #9ca3af; }
  html.dark .rte .ql-editor.ql-blank::before { color: #6b7280; }
  html.dark .rte .ql-editor .ql-callout { background: rgba(99,102,241,.15); }
  html.dark .rte .ql-snow .ql-tooltip { background: #1f2937; border-color: #374151; color: #e5e7eb; box-shadow: none; }
`;

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Start writing… Click “+ Add section” for each main topic of the article.',
}) => {
  const quillRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const toolbar = wrapRef.current?.querySelector('.ql-toolbar');
    if (!toolbar) return;
    Object.entries(TOOLTIPS).forEach(([selector, title]) => {
      toolbar.querySelectorAll(selector).forEach((el) => el.setAttribute('title', title));
    });
  }, []);

  const { sections, words } = useMemo(() => {
    const html = String(value || '');
    const found = [...html.matchAll(/<h2[^>]*>([\s\S]*?)<\/h2>/gi)].map((m) => stripTags(m[1])).filter(Boolean);
    const text = stripTags(html);
    return { sections: found, words: text ? text.split(' ').length : 0 };
  }, [value]);

  const jumpTo = (index) => {
    const heading = quillRef.current?.getEditor().root.querySelectorAll('h2')[index];
    if (heading) heading.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const readMinutes = Math.max(1, Math.round(words / 200));

  return (
    <div className="rich-text-editor rte" ref={wrapRef}>
      <style>{STYLES}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        placeholder={placeholder}
      />
      <div className="rte-footer">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-gray-600">
            In This Article
            <span className="font-normal text-gray-400">
              {' '}— {sections.length
                ? `${sections.length} section${sections.length === 1 ? '' : 's'}, shown as jump-links beside the article`
                : 'section headings appear here and as jump-links beside the article'}
            </span>
          </p>
          <p className="text-xs text-gray-400">
            {words} word{words === 1 ? '' : 's'}{words > 0 && ` · ${readMinutes} min read`}
          </p>
        </div>
        {sections.length > 0 ? (
          <ol className="mt-2 flex flex-wrap gap-1.5">
            {sections.map((title, i) => (
              <li key={`${i}-${title}`}>
                <button
                  type="button"
                  onClick={() => jumpTo(i)}
                  className="inline-flex items-center gap-1.5 max-w-[16rem] px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs text-gray-700 hover:border-primary-300 hover:text-primary-700"
                  title="Scroll to this section"
                >
                  <span className="font-semibold text-primary-600">{i + 1}</span>
                  <span className="truncate">{title}</span>
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-1 text-xs text-gray-400">
            No sections yet. Click <span className="font-semibold text-primary-600">+ Add section</span> in the toolbar to add one.
          </p>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
